import { Consumptions } from "@/components/balances/Consumptions";
import { Alert } from "@/components/fun-ui/Alert";
import { DeleteIcon } from "@/components/fun-ui/icons/DeleteIcon";
import { HelpCircleIcon } from "@/components/fun-ui/icons/HelpCircleIcon";
import { PlusIcon } from "@/components/fun-ui/icons/PlusIcon";
import { Tip } from "@/components/fun-ui/Tip";
import { useSubjectId } from "@/components/subjects/hooks/usePathParams";
import { cn } from "@/lib/utils";
import { useAiCol, useUserVip } from "@/stores/appStore";
import { Col, SetPaperColsMode } from "@/stores/slices/paperSlice";
import { useCallback, useMemo, Fragment, memo, useRef, useState } from "react";
import { FloatingPortal, offset } from "@floating-ui/react";
import { useFloatingCard } from "@/hooks/useFloatingCard";
import { AIColLimitFloatingCard, LimitType } from "./AIColLimitFloatingCard";
import { CreateCustomAIColButton } from "./CreateCustomAICol";
import { MinusIcon } from "@/components/fun-ui/icons";
import { Separator } from "@/components/ui/separator";
import { chartColKeys, chartColTips, LANG_LIST } from "./lib/aiColsConst";

const chartColKeySet = new Set(chartColKeys);
const langLabelByValue = new Map(LANG_LIST.map((item) => [item.value, item.label]));

export type AIColsSelectorProps = {
  cols: Col[];
  onSelected: (colKeys: string[], cols: Col[], mode?: SetPaperColsMode) => Promise<void>;
  className?: string;
};

type GroupedCols = {
  title: string;
  currentcols: Col[];
};

type AIColSelectorItemProps = {
  col: Col;
  isAiColsLimited: boolean;
  subjectId: string;
  deleteAiCol: ReturnType<typeof useAiCol>["deleteAiCol"];
  onSelectCol: (col: Col, mode?: SetPaperColsMode) => Promise<void>;
  onLimitTypeChange: (type: LimitType) => void;
  getReferenceHoverProps: ReturnType<typeof useFloatingCard>["getReferenceHoverProps"];
};

const AIColSelectorItem = memo(function AIColSelectorItem({
  col,
  isAiColsLimited,
  subjectId,
  deleteAiCol,
  onSelectCol,
  onLimitTypeChange,
  getReferenceHoverProps,
}: AIColSelectorItemProps) {
  const lang = langLabelByValue.get(col.lang);
  const isChartCol = chartColKeySet.has(col.key);
  const isSelected = col.selected;
  const isDisabled = !isSelected && isAiColsLimited && !isChartCol;

  const handleSelect = useCallback(async () => {
    if (isDisabled) return;
    await onSelectCol(col, isSelected ? 'del' : 'add');
  }, [col, isDisabled, isSelected, onSelectCol]);

  const handleLimitedHover = useCallback(() => {
    onLimitTypeChange('aicols');
  }, [onLimitTypeChange]);

  return (
    <li
      data-target
      className={cn(
        "space-x-2 flex items-center justify-between",
      )}
    >
      <div className="flex flex-1 items-center">
        <div
          className="flex items-center w-fit cursor-pointer"
        >
          <div
            className={cn(
              "py-1.5 inline-flex items-center text-primary hover:underline underline-offset-4",
              (isDisabled) && "opacity-50 hover:no-underline"
            )}
            {...(isDisabled ? getReferenceHoverProps(handleLimitedHover) : {})}
            onClick={handleSelect}
          >
            {
              isSelected ? (
                <MinusIcon />
              ) : (
                <PlusIcon className="-mt-[1px]" />
              )
            }
            <span className="inline-block leading-6">{col.name}</span>
          </div>
        </div>
        {
          col.isCustom ? (
            <>
              <Tip
                className="cursor-help ml-3 text-muted-foreground"
                side="top"
                label={`${lang}｜${col.desc}`}
              >
                <HelpCircleIcon />
              </Tip>
              <Alert
                title="删除 AI 表格列"
                description={
                  <>
                    <span className="block text-sm">确认删除 AI 表格列“{col.name}”吗？删除后该列数据将丢失且不可找回</span>
                    <span className="block text-sm text-annotation-foreground">表格列输出语言：{lang}</span>
                    <span className="block text-sm text-annotation-foreground">表格列提取要求：{col.desc}</span>
                  </>
                }
                confirmLabel="删除"
                confirmVariant="destructive"
                asyncConfirm={async () => {
                  await deleteAiCol({
                    colId: col.colId,
                  }, subjectId);
                }}>
                <DeleteIcon
                  className="ml-3 text-muted-foreground cursor-pointer"
                />
              </Alert>
            </>
          ) : null
        }
        {
          !isSelected && isChartCol ? (
            <Tip
              className="cursor-help ml-3 text-muted-foreground"
              side="top"
              label={chartColTips[col.key as (keyof typeof chartColTips)]}
            >
              <HelpCircleIcon />
            </Tip>
          ) : null
        }
      </div>
      {
        isSelected ? null : (
          <Consumptions className="ml-2" scene={{
            label: col.cost ? `新生成 ${col.cellCount} 个单元格消耗 ${col.cost} 个飞豆` : '无需消耗飞豆',
            price: col.cost,
          }} />
        )
      }
    </li>
  );
});

export function AIColsSelector({
  cols,
  onSelected,
  className = '',
}: AIColsSelectorProps) {
  const [limitType, setLimitType] = useState<LimitType>('custom');
  const {
    open,
    refs,
    floatingStyles,
    getReferenceHoverProps,
    getFloatingHoverProps,
  } = useFloatingCard({
    placement: "left-start",
    middleware: [offset(6)],
  });

  const createBtnRef = useRef<HTMLDivElement>(null);

  const {
    allColsList,
    customColCount,
    selectedNonChartColCount,
  } = useMemo(() => {
    const customAvailable: Col[] = [];
    const chartAvailable: Col[] = [];
    const presetAvailable: Col[] = [];
    const selectedCols: Col[] = [];
    let nextCustomColCount = 0;
    let nextSelectedNonChartColCount = 0;

    cols.forEach((col) => {
      const isChartCol = chartColKeySet.has(col.key);
      if (col.isCustom) {
        nextCustomColCount += 1;
      }
      if (col.selected) {
        selectedCols.push(col);
        if (!isChartCol) {
          nextSelectedNonChartColCount += 1;
        }
      } else if (col.isCustom) {
        customAvailable.push(col);
      } else if (isChartCol) {
        chartAvailable.push(col);
      } else {
        presetAvailable.push(col);
      }
    });

    const nextAllColsList: GroupedCols[] = [{
      title: '创建的 AI 表格列：',
      currentcols: customAvailable,
    }, {
      title: '智能解析表格列：',
      currentcols: chartAvailable,
    }, {
      title: '预设的 AI 表格列：',
      currentcols: presetAvailable,
    }, {
      title: '已开启的表格列',
      currentcols: selectedCols,
    }];

    return {
      allColsList: nextAllColsList,
      customColCount: nextCustomColCount,
      selectedNonChartColCount: nextSelectedNonChartColCount,
    };
  }, [cols]);

  const { deleteAiCol } = useAiCol();
  const subjectId = useSubjectId();

  const {
    vip,
    limits
  } = useUserVip();

  const isAiColsLimited = useMemo(() => {
    if (!limits) return true;
    if (limits.aiColLimit < 0) return false;

    return selectedNonChartColCount >= limits.aiColLimit;
  }, [limits, selectedNonChartColCount]);

  const handleColSelected = useCallback(async (col: Col, mode?: SetPaperColsMode) => {
    const selectedCols = cols.filter(({ key: colKey }) => (
      colKey === col.key
    ));
    const colKeys = selectedCols.map(({ key }) => key);
    await onSelected(colKeys, selectedCols, mode);
  }, [cols, onSelected]);

  const handleLimitTypeChange = useCallback((type: LimitType) => {
    setLimitType(type);
  }, []);

  if (!limits) {
    return (
      <></>
    );
  }

  return (
    <>
      {customColCount < limits.customLimit ? (
        <CreateCustomAIColButton />
      ) : (
        <div
          ref={createBtnRef}
          className="mt-3 max-w-fit"
          {...getReferenceHoverProps(() => {
            handleLimitTypeChange('custom');
          })}
        >
          <CreateCustomAIColButton disabled={true} />
        </div>
      )}
      <ul className={
        cn(
          "flex flex-col",
          "max-h-fit",
          className,
        )
      }>
        {
          allColsList.map(({ title, currentcols }) => {
            return (
              <Fragment key={title}>
                {
                  currentcols.length ? (
                    <>
                      {
                        currentcols?.[0]?.selected ? (
                          <li className="mt-4"><Separator /></li>
                        ) : null
                      }
                      <li className="text-sm py-1.5 mt-3.5">{title}</li>
                    </>
                  ) : null
                }
                {
                  currentcols.map((col) => (
                    <AIColSelectorItem
                      key={col.key}
                      col={col}
                      isAiColsLimited={isAiColsLimited}
                      subjectId={subjectId}
                      deleteAiCol={deleteAiCol}
                      onSelectCol={handleColSelected}
                      onLimitTypeChange={handleLimitTypeChange}
                      getReferenceHoverProps={getReferenceHoverProps}
                    />
                  ))
                }
              </Fragment>
            )
          })
        }
        <FloatingPortal>
          {open && (
            <div
              className="z-[51]"
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingHoverProps()}
            >
              <AIColLimitFloatingCard limitType={limitType} />
            </div>
          )}
        </FloatingPortal>
      </ul>
    </>

  );
}
